// BetaEntry — the gate shown at /app when the visitor is not authenticated.
//
// Four explicit paths:
//   1. Continue with Google  (persistent account, server beta-gated)
//   2. Continue with Apple   (persistent account, server beta-gated)
//   3. Continue with email   (email/password account, server beta-gated)
//   4. Tasting Stage         (session-only guest preview)
//
// It explains the trade so a beta tester chooses deliberately:
//   • logged in  → meals, preferences, shopping list and cook progress are saved
//   • Tasting    → temporary; nothing is remembered after the browser/tab closes
//
// Pure/presentational: all behaviour is injected via onLogin / onTasting so the
// component is trivially unit-testable. NomLoop owns the auth-state transitions.

import React from "react";
import {
  DEFAULT_C,
  screenStyle,
  cardStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
} from "./authStyles.js";

const providerIconWrap = {
  position: "absolute",
  left: 18,
  top: "50%",
  transform: "translateY(-50%)",
  width: 24,
  height: 24,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
};

function providerButtonStyle(base) {
  return {
    ...base,
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 52,
    paddingLeft: 54,
    paddingRight: 54,
  };
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ width: 22, height: 22 }}>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.15v2.84C3.96 20.53 7.68 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.15C1.41 8.53 1 10.22 1 12s.41 3.47 1.15 4.94l3.69-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.68 1 3.96 3.47 2.15 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ width: 23, height: 23 }}>
      <path
        fill="currentColor"
        d="M16.2 1.9c.1 1.2-.4 2.4-1.1 3.3-.8.9-2 1.6-3.1 1.5-.1-1.2.4-2.4 1.1-3.2.8-.9 2.1-1.6 3.1-1.6zm3.7 15.8c-.5 1.1-.8 1.6-1.5 2.5-1 1.4-2.3 3.1-4 3.1-1.5 0-1.9-1-3.9-1s-2.5 1-3.9 1c-1.7 0-3-1.6-4-3-2.7-3.8-3-8.3-1.3-10.7 1.2-1.7 3-2.7 4.7-2.7 1.8 0 2.9 1 4.3 1 1.4 0 2.3-1 4.3-1 1.5 0 3.1.8 4.3 2.2-3.8 2.1-3.2 7.5 1 8.6z"
      />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ width: 22, height: 22 }}>
      <path
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M4 6h16v12H4z"
      />
      <path
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="m4 7 8 6 8-6"
      />
    </svg>
  );
}

export default function BetaEntry({
  onGoogle,
  onApple,
  onEmail,
  onTasting,
  C = DEFAULT_C,
  logo = null,
}) {
  return (
    <div style={screenStyle(C)} data-testid="beta-entry">
      {logo}
      <h1
        style={{
          fontFamily: "Lora, Georgia, serif",
          fontSize: 32,
          fontWeight: 700,
          color: C.white,
          margin: "8px 0 8px",
        }}
      >
        NomLoop
      </h1>

      <p style={{ color: "rgba(255,255,255,.85)", fontSize: 15, lineHeight: 1.6, maxWidth: 420, marginBottom: 6 }}>
        Log in to save your meals, preferences, shopping list, and cook progress.
      </p>
      <p style={{ color: "rgba(255,255,255,.7)", fontSize: 14, lineHeight: 1.6, maxWidth: 420, marginBottom: 26 }}>
        Or enter Tasting Stage to look around without saving.
      </p>

      <div style={{ ...cardStyle, gap: 12 }}>
        <button
          type="button"
          onClick={onGoogle}
          style={providerButtonStyle(primaryButtonStyle(C))}
          data-testid="beta-entry-google"
        >
          <span style={providerIconWrap}><GoogleIcon /></span>
          <span>Continue with Google</span>
        </button>
        <button
          type="button"
          onClick={onApple}
          style={providerButtonStyle(secondaryButtonStyle())}
          data-testid="beta-entry-apple"
        >
          <span style={providerIconWrap}><AppleIcon /></span>
          <span>Continue with Apple</span>
        </button>
        <button
          type="button"
          onClick={onEmail}
          style={providerButtonStyle(secondaryButtonStyle())}
          data-testid="beta-entry-email"
        >
          <span style={providerIconWrap}><EmailIcon /></span>
          <span>Continue with email</span>
        </button>
        <button
          type="button"
          onClick={onTasting}
          style={secondaryButtonStyle()}
          data-testid="beta-entry-tasting"
        >
          Tasting Stage
        </button>
        <p style={{ color: "rgba(255,255,255,.6)", fontSize: 12.5, lineHeight: 1.5, margin: "2px 0 0" }}>
          Tasting Stage is temporary and won’t be remembered after you close this
          browser or tab.
        </p>
      </div>

      <a
        href="/"
        style={{ marginTop: 24, color: "rgba(255,255,255,.55)", fontSize: 13, textDecoration: "none" }}
      >
        ← Back to homepage
      </a>
    </div>
  );
}
